import invariant from 'tiny-invariant';
import type { EventListener } from 'xstate/lib/interpreter';
import { requestHub } from './machines/hub';
import {
  API_BASE,
  DraftValue,
  GetDoneMessage,
  PostDoneMessage,
  ServiceID,
  Version,
  VersionList,
  XCallerId,
} from './types';

let currentMessageID = 0;

// TODO: оптимизировать вызовы.

function setupListener(
  messageID: string,
  resolve: (response: any) => void,
  reject: (error: any) => void
) {
  let listener = (message: PostDoneMessage) => {
    if (message.type === 'done' && message.messageID === messageID) {
      if (message.response.code === 'SUCCESS') resolve(message.response);
      else reject(message.response);
      requestHub.off(listener);
    }
  };
  requestHub.onEvent(listener as EventListener);
}

function saveDraft(
  xCallerId: XCallerId,
  apiBase: API_BASE,
  serviceID: ServiceID,
  version: Version,
  data: DraftValue
): Promise<PostDoneMessage['response']> {
  return new Promise((resolve, reject) => {
    let messageID = String(currentMessageID++);
    setupListener(messageID, resolve, reject);
    let message = {
      xCallerId,
      apiBase,
      serviceID,
      version,
      type: 'save',
      messageID,
      data,
    } as const;
    requestHub.send(message);
  });
}

function loadDraft(
  xCallerId: XCallerId,
  apiBase: API_BASE,
  serviceID: ServiceID,
  version: Version
): Promise<GetDoneMessage['response']> {
  return new Promise((resolve, reject) => {
    let messageID = String(currentMessageID++);
    setupListener(messageID, resolve, reject);
    let message = {
      xCallerId,
      apiBase,
      serviceID,
      version,
      type: 'load',
      messageID,
    } as const;
    requestHub.send(message);
  });
}

function deleteDraft(
  xCallerId: XCallerId,
  apiBase: API_BASE,
  serviceID: ServiceID,
  version: Version
): Promise<PostDoneMessage['response']> {
  return new Promise((resolve, reject) => {
    let messageID = String(currentMessageID++);
    setupListener(messageID, resolve, reject);
    let message = {
      xCallerId,
      apiBase,
      serviceID,
      version,
      type: 'delete',
      messageID,
    } as const;
    requestHub.send(message);
  });
}

/**
 * @public
 */
export type Options = {
  /**
   * Идентификатор услуги в рамках приложения
   */
  serviceID: ServiceID;
  /**
   * Базовая часть API URL приложения
   * @example
   *
   * let baseURL = '/api/ums/v1/draft'
   */
  apiBase: API_BASE;
  /**
   * Идентификатор приложения в запросах. Отправляется в заголовке.
   */
  xCallerId: XCallerId;
};

/**
 * @public
 * Создаёт инстанс сервиса для работы с черновиками в рамках услуги внутри приложения
 * @param options - Опции для конфигурации запросов
 * @returns
 * @example
 ```ts
 import {configure} from '@mos-utils/drafts';

 let draft = configure({
    apiBase: '/api/ums/v1/draft',
    xCallerId: 'service::ums',
    serviceID: 'ums'
  });

 let autoDraft = await loadAuto();
 await saveAuto(draftValue);
 ```
 */
export function configure(options: Options) {
  let { serviceID, xCallerId, apiBase } = options;
  invariant(serviceID);
  invariant(xCallerId);
  invariant(apiBase);
  let autoVersion = `auto.${serviceID}`;
  return {
    /**
     * Создаёт _новый_ инстанс сервиса на основе существующих настроек
     * @param newOptions - Настройки нового инстанса. Будут смержены с текущими настройками
     * @returns
     */
    configure(newOptions: Partial<Options>) {
      return configure(Object.assign({}, options, newOptions));
    },
    fetchList(): Promise<{ code: 'SUCCESS'; list: VersionList }> {
      return new Promise((resolve, reject) => {
        return fetch(`${apiBase}/${serviceID}`, {
          headers: {
            'x-caller-id': xCallerId,
          },
        })
          .then((response) => response.json())
          .catch((error) => {
            return { code: 'ERROR_INTERNAL', message: error.message };
          })
          .then((responseBody) => {
            if (responseBody.code === 'SUCCESS')
              return resolve({
                code: responseBody.code,
                list: responseBody.data,
              });
            return reject(responseBody);
          });
      });
    },
    save: saveDraft.bind(undefined, xCallerId, apiBase, serviceID),
    load: loadDraft.bind(undefined, xCallerId, apiBase, serviceID),
    delete: deleteDraft.bind(undefined, xCallerId, apiBase, serviceID),
    saveAuto: saveDraft.bind(
      undefined,
      xCallerId,
      apiBase,
      serviceID,
      autoVersion
    ),
    loadAuto: loadDraft.bind(
      undefined,
      xCallerId,
      apiBase,
      serviceID,
      autoVersion
    ),
    deleteAuto: deleteDraft.bind(
      undefined,
      xCallerId,
      apiBase,
      serviceID,
      autoVersion
    ),
  };
}
